import { Button, IconButton, SliderModal, TextTypes, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import { Text, Box, ScrollView, IFlexProps, Flex, Pressable } from 'native-base';
import React from 'react';
import { Dimensions } from 'react-native';
import { useRecoilValue } from 'recoil';

import { useReportsActions } from '../../../../../../../store/customerDetail/reports/actions';
import {
  reportComplementaryTitleOptionsState,
  reportPeriodYearOptionState,
} from '../../../../../../../store/customerDetail/reports/atom';
import {
  reportsComplementaryTitles,
  reportsYears,
} from '../../../../../../../store/customerDetail/reports/constants';

import { ReportFilterTypeProps } from '.';

const { height } = Dimensions.get('window');

const textTitleStyle = {
  variant: 'semibold',
  fontSize: Typography['fontsize.md'],
} as TextTypes.Props;

const flexWrapperStyle = {
  flex: 1,
  direction: 'row' as const,
  style: { gap: 16 },
  flexWrap: 'wrap',
} as IFlexProps;

interface ItemSelectProps {
  label: string;
  value: string;
  selectedOption: string | string[];
  handleSelect: (value: string) => void;
}

const ItemSelect = ({ label, value, handleSelect, selectedOption }: ItemSelectProps) => {
  const selectedOptionIsArray = Array.isArray(selectedOption);

  const itemIsSelected = selectedOptionIsArray
    ? selectedOption.includes(value)
    : selectedOption === value;

  return (
    <Pressable
      borderRadius={BoxRadius.md}
      borderWidth={'0.5px'}
      borderColor="neutral.dark"
      py={Spacing['stack.quarck']}
      px={Spacing['inline.xxxs']}
      onPress={() => handleSelect(value)}
      {...(itemIsSelected && { bg: 'brand.primary.principal' })}>
      <Text
        fontSize={Typography['fontsize.xs']}
        color={itemIsSelected ? 'neutral.white' : 'neutral.dark'}>
        {label}
      </Text>
    </Pressable>
  );
};

export const ComplementaryReportsFilter: React.FC<ReportFilterTypeProps> = ({
  incomeTaxBaseYearReports,
  incomeTaxTypeReports,
  accountNumber,
}) => {
  const { goBack } = useNavigation();

  const {
    updateReportPeriodYearOption,
    updateReportComplementaryTitleOption,
    handleDownloadIncomeTaxComplementaryReport,
  } = useReportsActions();

  const reportYearOption = useRecoilValue(reportPeriodYearOptionState);
  const reportComplementaryTitleOption = useRecoilValue(reportComplementaryTitleOptionsState);

  const textButtonStyle = {
    fontFamily: Typography['fontfamily.base.semibold'],
    fontSize: Typography['fontsize.md'],
  };

  const reportComplementaryItem = incomeTaxTypeReports?.data?.find(
    (item) => item.value === reportComplementaryTitleOption
  );
  const fileTitle = reportComplementaryItem?.label || 'File';

  const handleApplyFilters = () => {
    handleDownloadIncomeTaxComplementaryReport({
      accountNumber,
      fileTitle,
      reportType: reportComplementaryTitleOption,
      yearReference: reportYearOption,
    });
    goBack();
  };

  const handleGoBack = () => {
    goBack();
  };

  const hasSelectItems =
    incomeTaxBaseYearReports?.data?.length > 0 && incomeTaxTypeReports?.data?.length > 0;

  const title = hasSelectItems
    ? 'Selecione o período:'
    : 'Ocorreu um erro!\nPor favor, tente novamente mais tarde.';

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={handleGoBack}
      direction={'bottom'}
      modalInsideModal>
      <VStack maxHeight={{ base: height * 0.8, lg: 'full' }} flex={1}>
        <VStack
          alignItems="center"
          w="full"
          p={Spacing['inline.xxs']}
          borderTopRadius={Spacing['inline.xxxs']}
          space={Spacing['inline.xxxs']}
          backgroundColor={Color['neutral.white']}>
          <Box w="55px" background="neutral.lightest" borderRadius={BoxRadius.sm} h={1} />
          <HStack w={'full'} alignItems="center" justifyContent="space-between">
            <Text variant="semibold" fontSize={Typography['fontsize.md']}>
              {title}
            </Text>
            <IconButton
              icon="Close"
              testId={'button-close'}
              iconColor={Color['neutral.dark']}
              iconSize="xxs"
              onPress={handleGoBack}
            />
          </HStack>
          {hasSelectItems && (
            <ScrollView
              showsVerticalScrollIndicator={false}
              backgroundColor={Color['neutral.white']}
              w={'full'}>
              <VStack space={Spacing['stack.xxxs']}>
                <Text {...textTitleStyle}>{reportsYears.title.modal}</Text>
                <Flex {...flexWrapperStyle}>
                  {incomeTaxBaseYearReports?.data?.map((year) => (
                    <ItemSelect
                      {...year}
                      key={year.label}
                      selectedOption={reportYearOption}
                      handleSelect={(value) => updateReportPeriodYearOption(value)}
                    />
                  ))}
                </Flex>
                <Text {...textTitleStyle}>{reportsComplementaryTitles.title.modal}</Text>
                <Flex {...flexWrapperStyle}>
                  {incomeTaxTypeReports?.data?.map((complementary) => (
                    <ItemSelect
                      {...complementary}
                      key={complementary.label}
                      selectedOption={reportComplementaryTitleOption}
                      handleSelect={(value) => updateReportComplementaryTitleOption(value)}
                    />
                  ))}
                </Flex>
              </VStack>
            </ScrollView>
          )}
          <Box w="full" background="neutral.lightest" borderRadius={BoxRadius.sm} h={0.1} />
          <Button
            w="full"
            variant={'primary'}
            onPress={handleApplyFilters}
            _text={textButtonStyle}
            isDisabled={!hasSelectItems}>
            Download
          </Button>
        </VStack>
      </VStack>
    </SliderModal>
  );
};
